import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import TimePicker from "../../../../components/TimePicker";

export type WeekendDaySchedule = {
  day: string;
  date: string;
  from: string;
  to: string;
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function toTimePickerValue(value?: string, fallback = "") {
  const raw = String(value || fallback || "").trim();
  if (!raw) return fallback;
  const parsed = moment(
    raw,
    [
      "hh : mm : ss a",
      "h : mm : ss a",
      "hh : mm a",
      "h : mm a",
      "hh:mm:ss a",
      "h:mm:ss a",
      "hh:mm a",
      "h:mm a",
      "HH:mm:ss",
      "HH:mm",
      "H:mm",
    ],
    false,
  );
  if (parsed.isValid()) return parsed.format("hh : mm a");
  return raw;
}

export function parseScheduleDate(value?: string | null): Date | null {
  if (!value || value === "Invalid date") return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : null;
}

export function formatScheduleDate(date: Date | null): string {
  if (!date || !moment(date).isValid()) return "";
  return moment(date)
    .tz("America/New_York", true)
    .format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)");
}

/** Derive Fri–Sun setup dates from projected `dateFrom` (America/New_York calendar). */
export function weekendScheduleFromDateFrom(
  dateFrom?: string | Date | null,
): {
  friday: WeekendDaySchedule;
  saturday: WeekendDaySchedule;
  sunday: WeekendDaySchedule;
} | null {
  if (!dateFrom) return null;
  const ny = moment(dateFrom).tz("America/New_York");
  if (!ny.isValid()) return null;
  const localNoon = (offset: number) => {
    const d = ny.clone().add(offset, "days");
    return new Date(d.year(), d.month(), d.date(), 12, 0, 0);
  };
  return {
    friday: { day: "Friday", date: formatScheduleDate(localNoon(0)), from: "", to: "" },
    saturday: { day: "Saturday", date: formatScheduleDate(localNoon(1)), from: "", to: "" },
    sunday: { day: "Sunday", date: formatScheduleDate(localNoon(2)), from: "", to: "" },
  };
}

function DateHeader({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: any) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 16 }, (_, i) => currentYear - 2 + i);
  return (
    <div style={{ textAlign: "center", paddingBottom: 8 }}>
      <div
        style={{
          marginBottom: 10,
          display: "flex",
          gap: 10,
          justifyContent: "center",
        }}
      >
        <select
          value={MONTHS[date.getMonth()]}
          onChange={({ target: { value } }) => changeMonth(MONTHS.indexOf(value))}
          style={{
            fontSize: 16,
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        >
          {MONTHS.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>
        <select
          value={date.getFullYear()}
          onChange={({ target: { value } }) => changeYear(Number(value))}
          style={{
            fontSize: 16,
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          {"<"}
        </button>
        <span>
          {MONTHS[date.getMonth()]} {date.getFullYear()}
        </span>
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          {">"}
        </button>
      </div>
    </div>
  );
}

export default function WeekendDayFields({
  day,
  weekday,
  value,
  onDateChange,
  onFromChange,
  onToChange,
  sameTimeError,
}: {
  day: string;
  weekday: number;
  value: WeekendDaySchedule;
  onDateChange: (date: Date | null) => void;
  onFromChange: (time: string | null) => void;
  onToChange: (time: string | null) => void;
  sameTimeError?: boolean;
}) {
  return (
    <div className="border rounded p-3">
      <h5 className="fw-bold mb-3">{day}</h5>
      <div className="mb-2">
        <label className="form-label">Date</label>
        <DatePicker
          autoComplete="off"
          dateFormat="MM-dd-yyyy"
          placeholderText="mm-dd-yyyy"
          className="form-select"
          wrapperClassName="w-100"
          selected={parseScheduleDate(value.date)}
          onChange={onDateChange}
          filterDate={(date) => date.getDay() === weekday}
          renderCustomHeader={DateHeader}
        />
      </div>
      <div className="d-flex gap-2">
        <div className="flex-fill">
          <label className="form-label">From</label>
          <TimePicker
            useTwelveHourFormat
            showClockIcon={false}
            showCloseIcon
            allowBackdrop
            placeholder="00 : 00 am"
            defaultValue={value.from}
            value={value.from}
            onTimeChange={onFromChange}
            className="timePicker"
          />
        </div>
        <div className="flex-fill">
          <label className="form-label">To</label>
          <TimePicker
            useTwelveHourFormat
            showClockIcon={false}
            showCloseIcon
            allowBackdrop
            placeholder="00 : 00 am"
            defaultValue={value.to}
            value={value.to}
            onTimeChange={onToChange}
            className="timePicker"
          />
        </div>
      </div>
      {sameTimeError ? (
        <p className="text-danger mt-2 mb-0">
          Time From and Time To should not be the same.
        </p>
      ) : null}
    </div>
  );
}
